import React from 'react';
import './Button.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Fills the parent container's width. */
  block?: boolean;
  /** Shows a spinner and marks the button aria-busy. Implies disabled. */
  loading?: boolean;
  /** Icon rendered before the label. */
  leadingIcon?: React.ReactNode;
  /** Icon rendered after the label. */
  trailingIcon?: React.ReactNode;
  /** HTML button type. Defaults to "button" to avoid accidental form submits. */
  type?: 'button' | 'submit' | 'reset';
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = 'primary',
      size = 'md',
      block,
      loading,
      leadingIcon,
      trailingIcon,
      children,
      className,
      disabled,
      type = 'button',
      ...rest
    },
    ref
  ) {
    const classes = [
      'bso-button',
      `bso-button--${variant}`,
      `bso-button--${size}`,
      block ? 'bso-button--block' : '',
      className ?? ''
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <button
        {...rest}
        ref={ref}
        type={type}
        className={classes}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
      >
        {loading ? (
          <span className="bso-button__spinner" aria-hidden="true" />
        ) : (
          leadingIcon
        )}
        {children}
        {!loading && trailingIcon}
      </button>
    );
  }
);
